// Balises <head> par page : titre, description, canonique, Open Graph,
// Twitter, robots et données structurées (JSON-LD).
//
// Pourquoi maison et pas react-helmet : il n'y a qu'un <head> et une
// poignée de balises. Le hook écrit directement dans document.head et se
// nettoie au démontage — c'est tout ce qu'il faut, sans dépendance.
//
// Au PRERENDU (scripts/prerender.js), l'effet tourne dans le Chrome sans
// tête et ses balises sont figées dans le HTML de build/<route>/index.html :
// c'est ainsi que Google et les aperçus de lien (Slack, iMessage, X…) les
// lisent sans exécuter le JavaScript. Au chargement client, le même effet
// retrouve les balises déjà présentes et les met simplement à jour.
import { useEffect } from 'react'

export const SITE = {
  origin: 'https://selfcntrl.com',
  name: 'SELFCNTRL',
  // Image de partage par défaut, 1200×630, générée par scripts/og-image.js.
  image: '/og.jpg',
  imageWidth: 1200,
  imageHeight: 630,
  locale: 'en_US',
  email: 'contact@selfcntrl.com'
}

export function absolute (path) {
  if (!path) return SITE.origin + '/'
  if (/^https?:\/\//.test(path)) return path
  return SITE.origin + (path.startsWith('/') ? path : '/' + path)
}

/** Ramène une chaîne de dictionnaire (indentée sur plusieurs lignes) à une ligne. */
export function oneLine (s) {
  return String(s ?? '').replace(/\s+/g, ' ').trim()
}

function upsertMeta (attr, key, content) {
  const head = document.head
  let el = head.querySelector(`meta[${attr}="${key}"]`)
  if (content == null || content === '') {
    if (el) el.remove()
    return
  }
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink (rel, href) {
  const head = document.head
  let el = head.querySelector(`link[rel="${rel}"]`)
  if (!href) {
    if (el) el.remove()
    return
  }
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function setJsonLd (blocks) {
  document.head.querySelectorAll('script[data-seo="ld"]').forEach(s => s.remove())
  for (const data of blocks) {
    if (!data) continue
    const s = document.createElement('script')
    s.type = 'application/ld+json'
    s.setAttribute('data-seo', 'ld')
    s.text = JSON.stringify(data)
    document.head.appendChild(s)
  }
}

/**
 * @param {object} o
 * @param {string}  o.title        Titre de la page (sans le nom du site).
 * @param {string}  o.description  150–160 caractères, c'est l'extrait Google.
 * @param {string}  o.path         Chemin canonique, ex. '/guides/quit-porn'.
 * @param {string}  [o.image]      Image de partage, chemin absolu ou relatif.
 * @param {string}  [o.type]       og:type — 'website' (défaut) ou 'article'.
 * @param {boolean} [o.noindex]    Pages privées ou sans valeur de recherche.
 * @param {object[]} [o.jsonLd]    Blocs JSON-LD, déjà sous forme d'objets.
 * @param {boolean} [o.bare]       Titre tel quel, sans « · SELFCNTRL ».
 * @param {string}  [o.published]  ISO date (article).
 * @param {string}  [o.modified]   ISO date (article).
 */
export function useSeo ({
  title, description, path, image, type = 'website', noindex = false,
  jsonLd = [], bare = false, published, modified
}) {
  const desc = oneLine(description)
  const fullTitle = bare ? title : `${title} · ${SITE.name}`
  const url = absolute(path)
  const img = absolute(image || SITE.image)
  // Les tableaux se recréent à chaque rendu : on compare leur contenu, pas
  // leur identité, sinon l'effet réécrirait le <head> à chaque frappe.
  const ldKey = JSON.stringify(jsonLd)

  useEffect(() => {
    document.title = fullTitle

    upsertMeta('name', 'description', desc)
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large')
    upsertLink('canonical', noindex ? null : url)

    upsertMeta('property', 'og:site_name', SITE.name)
    upsertMeta('property', 'og:locale', SITE.locale)
    upsertMeta('property', 'og:type', type)
    upsertMeta('property', 'og:title', fullTitle)
    upsertMeta('property', 'og:description', desc)
    upsertMeta('property', 'og:url', url)
    upsertMeta('property', 'og:image', img)
    upsertMeta('property', 'og:image:width', image ? null : String(SITE.imageWidth))
    upsertMeta('property', 'og:image:height', image ? null : String(SITE.imageHeight))
    upsertMeta('property', 'article:published_time', type === 'article' ? published : null)
    upsertMeta('property', 'article:modified_time', type === 'article' ? (modified || published) : null)

    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', fullTitle)
    upsertMeta('name', 'twitter:description', desc)
    upsertMeta('name', 'twitter:image', img)

    // L'organisation et le site sont déclarés sur CHAQUE page : les guides
    // désignent leur auteur et leur éditeur par référence (« @id »), et
    // Google lit chaque page isolément — sans le nœud, l'auteur n'a pas
    // de nom. Une page qui les fournit déjà (l'accueil) n'est pas doublée.
    const own = JSON.parse(ldKey)
    const ids = new Set(own.filter(Boolean).map(b => b['@id']))
    const base = [organizationLd(), websiteLd()].filter(b => !ids.has(b['@id']))
    setJsonLd(noindex ? own : [...base, ...own])

    return () => {
      // Une page sans <Seo> (le tableau de bord) ne doit pas hériter des
      // données structurées de la page précédente.
      setJsonLd([])
    }
  }, [fullTitle, desc, url, img, type, noindex, ldKey, image, published, modified])
}

// ---------------------------------------------------------------------------
//  Blocs JSON-LD réutilisables. Un seul endroit pour les faits sur
//  l'entreprise : une raison sociale qui change se corrige ici.
// ---------------------------------------------------------------------------

export const ORG_ID = SITE.origin + '/#organization'
export const SITE_ID = SITE.origin + '/#website'
export const APP_ID = SITE.origin + '/#software'

export function organizationLd () {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE.name,
    url: SITE.origin + '/',
    logo: {
      '@type': 'ImageObject',
      url: absolute('/icon-512.png'),
      width: 512,
      height: 512
    },
    email: SITE.email,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: SITE.email,
      availableLanguage: ['English', 'French']
    }
  }
}

export function websiteLd () {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': SITE_ID,
    name: SITE.name,
    url: SITE.origin + '/',
    inLanguage: 'en',
    publisher: { '@id': ORG_ID }
  }
}

/**
 * La fiche « logiciel ». Les prix sont ceux de la grille tarifaire — à
 * garder alignés sur locales/en/landing.js -> plans.
 *
 * ⚠️ PAS de `aggregateRating` tant que la note et le nombre d'avis affichés
 * sur la landing ne sont pas réels : une note inventée dans les données
 * structurées est une action manuelle Google garantie, et c'est vérifiable.
 */
export function softwareLd (description) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': APP_ID,
    name: SITE.name,
    url: SITE.origin + '/',
    description: oneLine(description),
    applicationCategory: 'SecurityApplication',
    applicationSubCategory: 'Content filter',
    operatingSystem: 'iOS, iPadOS, macOS, Windows',
    image: absolute(SITE.image),
    publisher: { '@id': ORG_ID },
    offers: [
      {
        '@type': 'Offer',
        name: 'Monthly',
        price: '9.99',
        priceCurrency: 'USD',
        category: 'subscription',
        url: SITE.origin + '/register?plan=monthly',
        availability: 'https://schema.org/InStock'
      },
      {
        '@type': 'Offer',
        name: 'Yearly',
        price: '49.99',
        priceCurrency: 'USD',
        category: 'subscription',
        url: SITE.origin + '/register?plan=yearly',
        availability: 'https://schema.org/InStock'
      }
    ],
    featureList: [
      'DNS-level porn filter on iPhone, iPad, Mac and Windows',
      'Supervised iPhone and iPad: the profile cannot be removed on impulse',
      'Covers every app and browser, private browsing included',
      'Night curfews that cut the internet on a schedule',
      'Straight cut or gradual taper, floor never drops',
      'No browsing history read or stored'
    ]
  }
}

export function faqLd (items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ q, a }) => ({
      '@type': 'Question',
      name: oneLine(q),
      acceptedAnswer: { '@type': 'Answer', text: oneLine(a) }
    }))
  }
}

export function breadcrumbLd (crumbs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: absolute(c.path)
    }))
  }
}

// « 2026-08-25 » -> « 2026-08-25T08:00:00+00:00 » : Google recommande une
// date complète avec fuseau ; une date nue est lue à minuit dans un fuseau
// deviné.
export function isoDate (d) {
  if (!d) return undefined
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T08:00:00+00:00` : d
}

export function articleLd ({ title, description, path, published, modified, image }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: oneLine(description),
    mainEntityOfPage: absolute(path),
    image: absolute(image || SITE.image),
    datePublished: isoDate(published),
    dateModified: isoDate(modified || published),
    inLanguage: 'en',
    isAccessibleForFree: true,
    // ⚠️ L'auteur est l'organisation, faute d'une personne réelle à nommer.
    // Google demande explicitement une signature (E-E-A-T, sujets santé) :
    // le jour où un fondateur ou un praticien accepte de signer, on passe à
    // { '@type': 'Person', name, url: '/about#…' } avec sa page ProfilePage.
    author: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
    about: { '@id': APP_ID }
  }
}

/**
 * La vidéo de présentation du hero (public/media/vsl.mp4 + vsl.jpg).
 * Google n'affiche une vignette vidéo dans les résultats que si la vidéo
 * est réellement visible sur la page ET décrite : durée, date, vignette,
 * fichier. Tout ici est vérifiable : 30,5 s mesurées par ffprobe, fichier
 * du 4 août 2026.
 */
export function videoLd () {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: 'How SELFCNTRL locks porn on iPhone, Mac and Windows',
    description: 'Thirty seconds: the filter, the supervised device that cannot be unlocked on impulse, the night curfew, and what the phone shows at two in the morning.',
    thumbnailUrl: [absolute('/media/vsl.jpg')],
    uploadDate: '2026-08-04',
    duration: 'PT31S',
    contentUrl: absolute('/media/vsl.mp4'),
    width: 848,
    height: 480,
    inLanguage: 'en',
    publisher: { '@id': ORG_ID }
  }
}
